import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Download, Linkedin, Mail } from "lucide-react";
import { profile } from "../data/content";
import { resumeUrl } from "../lib/access";

// Masked line-by-line reveal — the signature on-load moment.
const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  // One subtle parallax moment: the oversized monogram drifts as you scroll.
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const watermarkY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden border-b border-hairline" data-testid="hero-section">
      {/* Watermark monogram */}
      <motion.span
        aria-hidden="true"
        style={{ y: watermarkY }}
        className="pointer-events-none select-none absolute -right-6 top-8 font-display font-semibold text-[16rem] sm:text-[22rem] leading-none text-teal/[0.05]"
      >
        {profile.initials}
      </motion.span>

      <div className="container-x pt-20 pb-20 sm:pt-28 sm:pb-28 relative">
        <div className="grid lg:grid-cols-[1fr_250px] gap-12 lg:gap-16 items-center">
          <div>
        <MaskLine delay={0.1}>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-teal">
            Product · Diagnostics · Sequencing
          </span>
        </MaskLine>

        <h1 className="mt-8 font-display font-semibold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl text-ink" data-testid="hero-name">
          <MaskLine delay={0.22}>{profile.name}</MaskLine>
        </h1>

        <MaskLine delay={0.3} className="mt-4">
          <p className="text-lg md:text-xl text-ink font-semibold" data-testid="hero-fulltime-role">
            {profile.fullTimeRole}
          </p>
        </MaskLine>

        <MaskLine delay={0.36} className="mt-6">
          <p className="text-lg md:text-xl text-ink font-medium" data-testid="hero-role">
            {profile.roleLine}
          </p>
        </MaskLine>

        <MaskLine delay={0.48} className="mt-6 max-w-2xl">
          <p className="font-display text-xl sm:text-2xl text-body leading-snug" data-testid="hero-positioning">
            {profile.positioning}
          </p>
        </MaskLine>

        <MaskLine delay={0.58} className="mt-5 max-w-xl">
          <p className="text-base text-faint leading-relaxed">{profile.support}</p>
        </MaskLine>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.75 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <a
            data-testid="hero-resume-button"
            href={resumeUrl()}
            download
            className="inline-flex items-center gap-2 bg-teal text-canvas text-sm font-medium px-6 py-3 hover:bg-teal-deep transition-colors duration-300"
          >
            <Download size={15} /> Resume
          </a>
          <a
            data-testid="hero-linkedin-button"
            href={profile.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-cardline text-ink text-sm font-medium px-6 py-3 hover:border-teal hover:text-teal transition-colors duration-300"
          >
            <Linkedin size={15} /> LinkedIn
          </a>
          <a
            data-testid="hero-email-button"
            href={`mailto:${profile.email}`}
            className="inline-flex items-center gap-2 border border-cardline text-ink text-sm font-medium px-6 py-3 hover:border-teal hover:text-teal transition-colors duration-300"
          >
            <Mail size={15} /> Email
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-8 flex flex-wrap gap-2"
          data-testid="hero-chips"
        >
          {profile.chips.map((chip) => (
            <span
              key={chip}
              className="font-mono text-[11px] tracking-wide text-teal bg-teal-light px-3 py-1.5"
            >
              {chip}
            </span>
          ))}
        </motion.div>
          </div>

          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.7 }}
            className="relative max-w-[220px] lg:max-w-none"
            data-testid="hero-portrait"
          >
            <div className="border border-cardline bg-panel p-2.5">
              <img
                src="/ritu-mali.jpg"
                alt="Portrait of Ritu Mali"
                className="w-full aspect-[4/5] object-cover object-top"
              />
            </div>
            <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
              Ritu Mali — San Diego, CA
            </figcaption>
          </motion.figure>
        </div>
      </div>
    </section>
  );
}
